import { Suspense, useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { BrowserRouter, matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { createReactComponent } from '@dvag/design-system-react/dist/react-component-lib';
import { logout, sendTitle } from '@dvag/dfs-orchestrator-client/messengers';
import { navigateToMicroApp, syncOrchestratorUrl } from '@dvag/dfs-orchestrator-client/navigation';
import { Error403, Error404, ErrorCrashedApp } from '@dvag/dfs-ui-blocks/components';

import { appNames } from '@dvag/dfs-constant-config/app-names';
import { sharedAppRoutes } from '@dvag/dfs-constant-config/shared-app-names';
import { HttpStatusCode } from 'axios';
import { TError } from 'config/reactQuery';
import { route } from 'config/route';
import i18next from 'i18next';
import { AdvisoryDashboard } from 'screen/AdvisoryDashboard/AdvisoryDashboard';
import { AnalyseDashboard } from 'screen/AnalyseDashboard/Dashboard';
import { HouseholdDashboard } from 'screen/HouseholdDashboard/HouseholdDashboard';
import { RecommendationPage } from 'screen/RecommendationPage/RecommendationPage';
import { RecommendationPresentation } from 'screen/RecommendationPresentation/RecommendationPresentation';
import { Favorites } from './screen/Favorites/Favorites';
import { SettingPage } from './screen/SettingPage/SettingPage';
import { EvaluateAfter } from './screen/onGoodCooperation/EvaluateAfter/EvaluateAfter';
import { EvaluateBefore } from './screen/onGoodCooperation/EvaluateBefore/EvaluateBefore';
import { Recruiting } from './screen/onGoodCooperation/Recruiting/Recruiting';
import { WishesAndReferrals } from './screen/onGoodCooperation/WishesAndReferrals/WishesAndReferrals';
import { getMonitoring } from './service/getMonitoring';

const {
  home,
  householdDashboard,
  householdDashboardId,
  householdDashboardIdSection,
  advisoryDashboardIdSection,
  search,
  placeholder,
  teamBuilding,
  teamBuildingWithoutHH,
  recommendationPage,
  recommendationPageSelectedSection,
  recommendationPresentation,
  teamBuildingPresentation,
  advisoryDashboard,
  analyseDashboard,
  analyseDashboardSection,
  favorites,
  evaluateBefore,
  evaluateAfter,
  wishesAndReferrals,
  recruiting,
  setting,
  settingId,
} = route;

const titleTranslations: Record<string, string> = {
  householdDashboard: i18next.t('dashboard.welcome'),
  householdDashboardId: i18next.t('dashboard.clientWelcome'),
  householdDashboardIdSection: i18next.t('dashboard.clientWelcome'),
  advisoryDashboardIdSection: i18next.t('advisoryDashboard.headline'),
  search: i18next.t('dashboard.welcome'),
  placeholder: i18next.t('dashboard.welcome'),
  teamBuilding: i18next.t('recommendationPage.recommendation'),
  teamBuildingWithoutHH: i18next.t('dreamJobPage.dreamJob'),
  recommendationPage: i18next.t('recommendationPage.recommendation'),
  recommendationPageSelectedSection: i18next.t('recommendationPage.recommendation'),
  recommendationPresentation: i18next.t('recommendationPage.recommendation'),
  teamBuildingPresentation: i18next.t('dreamJobPage.dreamJob'),
  advisoryDashboard: i18next.t('advisoryDashboard.headline'),
  analyseDashboard: i18next.t('analyseDashboard.headline'),
  analyseDashboardSection: i18next.t('analyseDashboard.headline'),
  favorites: i18next.t('favorites.headline'),
  evaluateBefore: i18next.t('OGC.interviewConclusion.evaluateBefore.title'),
  evaluateAfter: i18next.t('OGC.interviewConclusion.evaluateAfter.title'),
  wishesAndReferrals: i18next.t('OGC.wishesAndReferrals.title'),
  recruiting: i18next.t('OGC.interviewConclusion.recruitPage.title'),
};

const navigateToAdvisorDashboard = () => {
  navigateToMicroApp(appNames.advisor, sharedAppRoutes.advisor.householdSearch);
};

const App = () => {
  const isInitialMount = useRef(true);
  const location = useLocation();

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (location.key) {
      syncOrchestratorUrl(`${location.pathname}`);
    }

    const currentPath = Object.keys(route).find((key) => matchPath(route[key], location.pathname));

    if (currentPath) {
      sendTitle(titleTranslations[currentPath] ? titleTranslations[currentPath] : 'Berater');
    }
  }, [location]);

  const fallbackComponent = ({ error }: FallbackProps) => {
    const { response } = error as TError;
    if ([response?.status, error.cause, error.status].includes(HttpStatusCode.Forbidden)) {
      return <Error403 onAction={navigateToAdvisorDashboard} />;
    }
    if ([response?.status, error.cause, error.status].includes(HttpStatusCode.NotFound)) {
      return <Error404 onAction={navigateToAdvisorDashboard} />;
    }
    return <ErrorCrashedApp onAction={navigateToAdvisorDashboard} />;
  };

  const errorHandler = (error: Error) => {
    getMonitoring().trackException(error);
    const { response } = error as TError;
    if (response?.status === HttpStatusCode.Unauthorized) {
      logout();
    }
  };

  const KundenzugangWebcomponent = useMemo(
    () => createReactComponent('kundenzugang-webcomponent'),
    [],
  );

  return (
    <Suspense fallback={null}>
      <ErrorBoundary
        FallbackComponent={fallbackComponent}
        onError={errorHandler}
        resetKeys={[location]}
      >
        {location.pathname !== '/haushalt' && <KundenzugangWebcomponent />}
        <Routes>
          <Route path={analyseDashboard} element={<AnalyseDashboard />} />
          <Route path={analyseDashboardSection} element={<AnalyseDashboard />} />
          <Route path={recommendationPage} element={<RecommendationPage teamBuilding={false} />} />
          <Route
            path={recommendationPageSelectedSection}
            element={<RecommendationPage teamBuilding={false} />}
          />
          <Route path={teamBuilding} element={<RecommendationPage teamBuilding />} />
          <Route path={teamBuildingWithoutHH} element={<RecommendationPage teamBuilding />} />
          <Route
            path={recommendationPresentation}
            element={<RecommendationPresentation teamBuilding={false} />}
          />
          <Route
            path={teamBuildingPresentation}
            element={<RecommendationPresentation teamBuilding />}
          />
          <Route path={search} element={<HouseholdDashboard />} />
          <Route path={placeholder} element={<HouseholdDashboard />} />
          <Route path={householdDashboard} element={<HouseholdDashboard />} />
          <Route path={householdDashboardId} element={<HouseholdDashboard />} />
          <Route path={householdDashboardIdSection} element={<HouseholdDashboard />} />
          <Route path={home} element={<Navigate to={householdDashboard} />} />
          <Route path={advisoryDashboard} element={<AdvisoryDashboard />} />
          <Route path={advisoryDashboardIdSection} element={<AdvisoryDashboard />} />
          <Route path={favorites} element={<Favorites />} />
          <Route path={evaluateBefore} element={<EvaluateBefore />} />
          <Route path={evaluateAfter} element={<EvaluateAfter />} />
          <Route path={wishesAndReferrals} element={<WishesAndReferrals />} />
          <Route path={recruiting} element={<Recruiting />} />
          <Route path={setting} element={<SettingPage />} />
          <Route path={settingId} element={<SettingPage />} />
          <Route path="*" element={<Error404 onAction={navigateToAdvisorDashboard} />} />
        </Routes>
      </ErrorBoundary>
    </Suspense>
  );
};

export const Router = () => (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);
